import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { isValid } from 'date-fns';

import { useConversionEventsMutation } from '@/api/hooks/mutations/use-conversion-events-mutation';
import { useCreateOrderMutation } from '@/api/hooks/mutations/use-create-order-mutation';
import { useSessionSave } from '@/hooks/use-session-save';
import { setExitMilestone, useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { trackOrderRejected } from '@/lib/analytics/events';
import { ApiError } from '@/lib/errors';
import { logger } from '@/lib/logger';
import { MILESTONES } from '@/lib/wizard/milestones';
import { getActionableInsurances } from '@/types/insurance-builder.type.ts';
import type { Order } from '@/types/order.type';
import { useGetPersistedContact } from '@/views/hooks/use-get-persisted-contact.ts';
import { useOrderStore } from '@/views/hooks/use-order-store.ts';

type CreateOrderContextValue = {
  createOrder: () => Promise<Order | null | undefined>;
  mutation: ReturnType<typeof useCreateOrderMutation>;
};

const CreateOrderContext = createContext<CreateOrderContextValue | null>(null);

function CreateOrderProvider({ children }: React.PropsWithChildren) {
  const { saveSession } = useSessionSave();

  const create = useCreateOrderMutation({
    onSuccess: (order, variables) => {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!order.appointment) {
        logger.error(new Error('Order created without appointment'), {
          orderId: order.id,
          scheduledTime: variables.scheduledTime,
          browserTimezone,
          productIds: variables.productIds,
        });
      } else if (!order.appointment.scheduledTime || !isValid(order.appointment.scheduledTime)) {
        logger.error(new Error('Order created with invalid scheduled time'), {
          orderId: order.id,
          scheduledTime: variables.scheduledTime,
          browserTimezone,
          productIds: variables.productIds,
          appointment: {
            scheduledTime: order.appointment.scheduledTime,
          },
        });
      }

      useOrderStore.setState({ order });

      // Set and persist the exit milestone
      setExitMilestone(MILESTONES.ORDER_COMPLETED);

      conversionEventsMutation.mutate({ ...contact, eventName: 'CompleteRegistration' });

      saveSession();
    },
    onError: (error, variables) => {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      logger.error('Failed to create order', error, {
        scheduledTime: variables.scheduledTime,
        browserTimezone,
        productIds: variables.productIds,
        responseData: error instanceof ApiError ? error.responseData : undefined,
      });

      trackOrderRejected();

      useOrderStore.setState({ order: null });
    },
  });

  const conversionEventsMutation = useConversionEventsMutation();
  const contact = useGetPersistedContact();

  // Synchronous flag to prevent duplicate submissions — React state updates
  // are async and can't guard against rapid successive calls in the same
  // render cycle.
  const isCreatingRef = useRef(false);

  const createOrder = useCallback(async () => {
    if (isCreatingRef.current) {
      return;
    }
    isCreatingRef.current = true;

    try {
      const state = useSessionStore.getState();

      const patient = {
        recipient: state.patientRecipient as 'me' | 'someone-else',
        firstName: state.patientFirstName,
        lastName: state.patientLastName,
        dateOfBirth: state.patientDateOfBirth,
        gender: state.patientGender as 'male' | 'female',
      };

      const address = {
        addressLine1: state.contactAddressLine1,
        addressLine2: state.contactAddressLine2,
        city: state.contactCity,
        state: state.contactState,
        zip: state.contactZip,
      };

      return await create.mutateAsync({
        productIds: state.selectedProductIds,
        providerId: state.schedulingSelectedProviderId,
        patient,
        address,
        insurances: getActionableInsurances(state.insuranceBuilder),
        insuranceGated: state.wizardVariantId === 'eligibility-revamp',
        contact,
        scheduledTime: state.schedulingSelectedSlot,
        telehealthContactType: state.schedulingSelectedContactType,
        telehealthAppointmentTypeId: state.schedulingSelectedAppointmentTypeId,
      });
    } catch (error) {
      isCreatingRef.current = false;
      throw error;
    }
  }, [contact, create]);

  const value = useMemo(
    () => ({
      createOrder,
      mutation: create,
    }),
    [createOrder, create]
  );

  return <CreateOrderContext.Provider value={value}>{children}</CreateOrderContext.Provider>;
}

function useCreateOrder() {
  const context = useContext(CreateOrderContext);

  if (!context) {
    throw new Error('useCreateOrder must be used within a CreateOrderProvider');
  }
  return context;
}

export { CreateOrderProvider, useCreateOrder };
